import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { pushTokenService } from '@/services/api';

// Token FCM deste aparelho (chega no evento 'registration'). Fica salvo no aparelho, não só em memória: se o app
// abrir já com a sessão expirada, o logout acontece antes de um novo 'registration' e ainda precisa do token.
const CHAVE_TOKEN_APARELHO = 'kora:push-token';

const lerTokenDoAparelho = (): string | null => {
  try {
    return localStorage.getItem(CHAVE_TOKEN_APARELHO);
  } catch {
    return null;
  }
};

const guardarTokenDoAparelho = (token: string | null) => {
  try {
    if (token) localStorage.setItem(CHAVE_TOKEN_APARELHO, token);
    else localStorage.removeItem(CHAVE_TOKEN_APARELHO);
  } catch {
    // sem storage: o logout só não consegue desvincular o aparelho
  }
};

/**
 * Logout: pede ao backend para desvincular este aparelho, senão ele seguiria recebendo os pushes de quem saiu.
 * A rota não exige sessão válida (o logout também acontece com a sessão expirada ou derrubada).
 */
export function removerTokenPushDoAparelho(): void {
  const token = lerTokenDoAparelho();
  if (!token) return;
  guardarTokenDoAparelho(null);
  void pushTokenService.remove(token);
}

/**
 * Registra o device para push notifications (Android/iOS via Capacitor) e
 * envia o token ao backend. No navegador (web) é um no-op — push nativo
 * só funciona dentro do app gerado pelo Capacitor.
 */
export function usePushNotifications(userId?: string) {
  useEffect(() => {
    if (!userId || !Capacitor.isNativePlatform()) return;

    let cancelled = false;

    // Enquanto o Firebase (google-services.json) não estiver configurado no projeto
    // nativo, qualquer chamada ao plugin pode lançar uma exceção nativa (FirebaseApp
    // não inicializado). Todo o fluxo é isolado em try/catch para nunca derrubar o app.
    const setup = async () => {
      try {
        const permission = await PushNotifications.checkPermissions();
        let status = permission.receive;
        if (status === 'prompt') {
          const requested = await PushNotifications.requestPermissions();
          status = requested.receive;
        }
        if (status !== 'granted' || cancelled) return;

        await PushNotifications.register();
      } catch (err) {
        console.error('Push notifications indisponível (Firebase provavelmente não configurado):', err);
      }
    };

    let registrationListener: ReturnType<typeof PushNotifications.addListener> | undefined;
    let registrationErrorListener: ReturnType<typeof PushNotifications.addListener> | undefined;
    let actionListener: ReturnType<typeof PushNotifications.addListener> | undefined;

    try {
      registrationListener = PushNotifications.addListener('registration', (token) => {
        guardarTokenDoAparelho(token.value);
        pushTokenService.upsert({
          token: token.value,
          platform: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
        });
      });

      registrationErrorListener = PushNotifications.addListener('registrationError', (err) => {
        console.error('Erro ao registrar push notifications:', err);
      });

      actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const route = action.notification.data?.route as string | undefined;
        if (route) window.location.assign(route);
      });

      setup();
    } catch (err) {
      console.error('Push notifications indisponível (Firebase provavelmente não configurado):', err);
    }

    return () => {
      cancelled = true;
      registrationListener?.then((l) => l.remove()).catch(() => {});
      registrationErrorListener?.then((l) => l.remove()).catch(() => {});
      actionListener?.then((l) => l.remove()).catch(() => {});
    };
  }, [userId]);
}
